import { useContext } from 'react';
import { useAuthenticated, useDataProvider, useNotify } from 'react-admin';

import { AppContext } from '@app/contexts';
import { useGenericList } from '@app/hooks/useGenericList';
import { CASLAction, useAbility } from '@app/utils/authUtils';
import { getFieldOnMainLanguage } from '@app/utils/languages';

import { GenericList } from '@app/components/common/GenericList';
import { ASPECT_RATIO_TYPES } from '@app/components/common/ImageSelector/ImageSelector';
import { ColumnTypes, DeleteConfirmation, GenericObject } from '@app/components/common/Table/DefaultTable/types';

import { CTA_TARGET_TYPE } from '.';

const formatTargetType = (value: string) => {
  const readable = {
    [CTA_TARGET_TYPE.EXTERNAL_LINK]: 'External Link',
    [CTA_TARGET_TYPE.MEDIA]: 'Media',
  };

  return readable[value];
};

export const ListCTA = () => {
  const { handleChangeLoading } = useContext(AppContext);
  const dataProvider = useDataProvider();
  const ability = useAbility();
  const notify = useNotify();

  const {
    isLoading,
    data,
    total,
    handleChangeSort,
    handleChangeFilters,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useGenericList('ctas');

  /**
   * List[X] pages require manual auth check for some reason.
   * This involves the `checkAuth` method in auth.ts.
   */
  useAuthenticated();
  const resolveDeleteConfirmationContent = async (ctaId: number): Promise<DeleteConfirmation> => {
    const response = await dataProvider.fetchLinks('ctas', ctaId);
    const links = response?.data;
    if (links.Rails > 0) {
      notify(`This CTA is associated with ${links.Rails} rail(s). Remove the CTA from all rails and try again.`, {
        type: 'error',
      });
      return { deletable: false };
    }

    return { deletable: true };
  };

  const columns = [
    {
      label: 'HEADING',
      key: 'heading',
      aspectRatio: ASPECT_RATIO_TYPES.THUMBNAIL,
      getImagePath: (record: GenericObject) => `${(record?.cta_image_file_base as GenericObject)?.path}`,
      type: ColumnTypes.TITLE,
      format: (row: GenericObject) => `${getFieldOnMainLanguage(row?.heading)}`,
      sort: (order: string) => {
        handleChangeSort({ field: 'heading', order });
      },
    },
    {
      label: 'SUB-HEADING',
      key: 'sub_heading',
      type: ColumnTypes.DEFAULT,
      format: (row: GenericObject) => `${getFieldOnMainLanguage(row?.sub_heading)}`,
      sort: (order: string) => {
        handleChangeSort({ field: 'sub_heading', order });
      },
    },
    {
      label: 'TARGET',
      key: 'target_type',
      type: ColumnTypes.DEFAULT,
      format: (row: GenericObject) => `${formatTargetType(row.target_type as string)}`,
    },
    {
      label: 'TOPIC',
      key: 'related_topic',
      type: ColumnTypes.DEFAULT,
      format: (row: GenericObject) => `${getFieldOnMainLanguage((row?.related_topic as GenericObject)?.category_name)}`,
      sort: (order: string) => {
        handleChangeSort({ field: 'RelatedTopic.category_name', order });
      },
    },
  ];

  if (ability.can(CASLAction.WRITE, 'ctas')) {
    columns.push({
      label: 'PUBLISHED',
      key: 'is_published',
      type: ColumnTypes.STATUS,
      // @ts-ignore this is definitely valid
      publishedKey: 'is_published',
      isPublished: (row: GenericObject) => !!row.is_published,
      getNewPublishedStatus: (row: GenericObject) => !row.is_published,
    });
  }

  return (
    <GenericList
      isListLoading={isLoading}
      rows={data}
      generalFilterKey={'name'}
      resourceSingular="CTA"
      handleChangeLoading={handleChangeLoading}
      columns={columns}
      resource="ctas"
      title="CTA"
      page={page}
      rowsPerPage={rowsPerPage}
      handleChangePage={handleChangePage}
      total={total}
      handleChangeRowsPerPage={handleChangeRowsPerPage}
      handleChangeFilters={handleChangeFilters}
      resolveDeleteConfirmationContent={resolveDeleteConfirmationContent}
    />
  );
};
